// Shared helpers for the Messaging / Outreach screen. Pulled out of index.tsx so
// the board, the list rows, and the detail drawer all read leads the same way.

import {
  OutreachLead,
  StageDef,
  StageConfig,
  DEFAULT_STAGE_CONFIG,
} from "../../../../types/OutreachMetrics";

export const CHANNEL_LABELS: Record<string, string> = {
  li: "LinkedIn",
  ig: "Instagram",
  em: "Email",
  x: "X",
  tw: "X",
};
export const channelLabel = (ch: string) =>
  CHANNEL_LABELS[ch?.toLowerCase()] || (ch ? ch.toUpperCase() : "Other");

// ─── Stage model (built from the account's editable stage config) ─────────────
// Stages used to be a fixed enum with union-keyed lookup maps. They're now
// per-account data, so every stage lookup (label, color, order, "is this the
// last funnel step") goes through a StageModel built from the loaded config.
// Unknown keys (a lead on a stage that was since renamed/deleted) degrade
// gracefully: humanized label, neutral tone, treated as active, never in the
// funnel.

// A stage's tone maps to a Tag variant (the chip) and a dot color (the column
// header). Tag only knows a few variants, so "info" collapses to neutral for the
// chip while keeping its own dot color.
export const TONE_VARIANT: Record<string, string> = {
  neutral: "neutral",
  info: "neutral",
  accent: "accent",
  success: "success",
  warning: "warning",
  danger: "danger",
};

export const TONE_DOT: Record<string, string> = {
  neutral: "var(--color-text-tertiary)",
  info: "var(--color-info, var(--color-text-secondary))",
  accent: "var(--color-accent)",
  success: "var(--color-success)",
  warning: "var(--color-warning)",
  danger: "var(--color-danger)",
};

export const humanizeStage = (key: string) =>
  key.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export interface StageModel {
  stages: StageDef[]; // configured, in order
  keys: string[]; // all configured keys, in order
  funnelKeys: string[]; // advance / funnel order (excludes negative-outcome terminals)
  funnelStages: StageDef[];
  positiveTerminals: string[]; // "won"-style
  negativeTerminals: string[]; // "lost"-style
  byKey: Record<string, StageDef>;
  label: (key: string) => string;
  variant: (key: string) => string;
  dot: (key: string) => string;
  isActive: (key: string) => boolean; // not terminal (unknown keys => active)
  isKnown: (key: string) => boolean;
  funnelIndex: (key: string) => number; // index within funnelKeys, -1 if outside
  nextStage: (key: string) => string | null; // next funnel stage, or null at the end
}

const isNegativeTerminal = (s: StageDef) =>
  !!s.terminal && s.outcome === "negative";

export function buildStageModel(config?: StageConfig | null): StageModel {
  const stages =
    config?.stages && config.stages.length
      ? config.stages
      : DEFAULT_STAGE_CONFIG.stages;
  const byKey: Record<string, StageDef> = {};
  stages.forEach((s) => (byKey[s.key] = s));
  const keys = stages.map((s) => s.key);
  const funnelStages = stages.filter((s) => !isNegativeTerminal(s));
  const funnelKeys = funnelStages.map((s) => s.key);
  return {
    stages,
    keys,
    funnelKeys,
    funnelStages,
    positiveTerminals: stages
      .filter((s) => s.terminal && s.outcome === "positive")
      .map((s) => s.key),
    negativeTerminals: stages
      .filter((s) => s.terminal && s.outcome === "negative")
      .map((s) => s.key),
    byKey,
    label: (k) => byKey[k]?.label || humanizeStage(k),
    variant: (k) => TONE_VARIANT[byKey[k]?.tone || "neutral"] || "neutral",
    dot: (k) => TONE_DOT[byKey[k]?.tone || "neutral"] || TONE_DOT.neutral,
    isActive: (k) => (byKey[k] ? !byKey[k].terminal : true),
    isKnown: (k) => !!byKey[k],
    funnelIndex: (k) => funnelKeys.indexOf(k),
    nextStage: (k) => {
      const i = funnelKeys.indexOf(k);
      return i >= 0 && i < funnelKeys.length - 1 ? funnelKeys[i + 1] : null;
    },
  };
}

// A lead whose current step is owned by a machine (Instantly cold-email queue,
// LinkedIn drip) waits on the automation, not on a human — the Today
// queue keeps it out of the follow-up sections. Durable contract: the pipeline
// marks data.automated=true (or data.owner="automation") on steps it owns; the
// text match covers leads written before that convention existed.
export const isAutomatedStep = (l: OutreachLead): boolean => {
  const d = (l as { data?: Record<string, unknown> }).data;
  if (d && (d.automated === true || d.owner === "automation")) return true;
  return /queued in /i.test(l.nextAction || "");
};

export const dueOf = (l: OutreachLead) =>
  l.nextActionAt ? l.nextActionAt.slice(0, 10) : undefined;

// Sentinel keeps no-date leads at the end of a due-ascending sort.
export const dueSortKey = (l: OutreachLead) => dueOf(l) || "9999-99-99";

export type DueTone = "overdue" | "today" | "future" | "none";
export const dueTone = (
  due: string | undefined,
  today: string
): DueTone =>
  !due ? "none" : due < today ? "overdue" : due === today ? "today" : "future";

const stripAt = (s: string) => s.replace(/^@+/, "");
const isUrl = (s: string) => /^https?:\/\//i.test(s);

// Where to go to actually message this lead — the platform owns the conversation,
// so we deep-link there rather than trying to send from inside the dashboard.
export const contactLink = (
  lead: OutreachLead
): { href: string; label: string } | null => {
  const ch = (lead.channel || "").toLowerCase();
  const h = (lead.handle || "").trim();
  const email = (lead.email || "").trim();
  if (ch === "li" && h)
    return {
      href: isUrl(h) ? h : `https://www.linkedin.com/in/${stripAt(h)}`,
      label: "Message on LinkedIn",
    };
  if (ch === "ig" && h)
    return {
      href: isUrl(h) ? h : `https://instagram.com/${stripAt(h)}`,
      label: "Message on Instagram",
    };
  if ((ch === "em" || ch === "email") && email)
    return { href: `mailto:${email}`, label: "Send email" };
  if (h && isUrl(h)) return { href: h, label: "Open profile" };
  if (email.includes("@"))
    return { href: `mailto:${email}`, label: "Send email" };
  return null;
};
